import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Drawer,
    Snackbar,
    Alert,
    Chip,
    GlobalStyles,
    FormControlLabel,
    Switch,
    Tooltip,
    Collapse,
    Stack,
    Modal,
    Backdrop,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useParams } from 'react-router-dom';

// Import sub-components
import ImageGallery from './ImageGallery';
import DescriptionSection from './DescriptionSection';
import DeferredCostSection from './DeferredCostSection';
import LocationSection from './LocationSection';
import StatusTracker from './StatusTracker';
import CommentsSection from './CommentsSection';
import NextActionButton from './NextActionButton';
import DeficiencyHistory from './DeficiencyHistory';

// Import the deficiency context hook
import { useDeficiencies } from '../../../Pages/Deficiencies/DeficiencyContext';

// Import NotificationBanner
import { NotificationBanner } from '../../../../NotificationSystem';

// Import usePermissions hook for role-based UI gating
import { usePermissions } from '../../../../hooks/usePermissions';

// Import date formatting utility
import { formatDate } from '../../../utils/dateUtils';
import { isInternalManagerRole, canSeeDeferredCost } from '../../../../utils/roles';

/**
 * Global component for displaying detailed deficiency information in a sidebar drawer
 * Can be used in any page that needs to show deficiency details
 *
 * Image Deletion Implementation:
 * This component uses refs to communicate with child ImageGallery components.
 * When a user clicks the delete button on an image:
 * 1. The image is visually removed from the UI immediately
 * 2. The actual deletion API call is only made when the user clicks "Save Changes"
 * 3. If the user clicks "Cancel", the images reappear and no API calls are made
 *
 * This pattern allows for:
 * - Consistent user experience (all changes apply only when saved)
 * - Batch deletion of multiple images in a single save operation
 * - Ability to cancel deletion operations before they're committed
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Object} props.deficiency - The deficiency object to display
 * @param {Function} props.onClose - Callback when the drawer is closed
 * @param {Function} props.getStatusChipProps - Function to get styling for status chips
 * @param {boolean} [props.readOnly=false] - Whether the sidebar is read-only
 * @param {React.ReactNode} [props.headerActions] - Additional action buttons to render in the header
 * @returns {React.ReactElement} Deficiency detail sidebar
 */
const DeficiencySidebar = ({
    open,
    deficiency,
    onClose,
    getStatusChipProps,
    readOnly = false,
    headerActions,
    onStatusChange,
    scrollToRejection = false,
    rejectionAttemptId = null,
}) => {
    // Get context functions and state
    const {
        updateDeficiencyImages,
        deleteDeficiencyImage,
        updateDeficiency,
        getImageUrl,
        state: deficiencyState,
    } = useDeficiencies();

    // Get projectId from URL params
    const { projectId } = useParams();

    // Get role from usePermissions hook for role-based UI gating
    const { getProjectRole } = usePermissions();
    const role = getProjectRole(projectId);

    // Role categorization helpers
    const isManager = isInternalManagerRole(role);
    // Trades include INTERNAL_TRADE, EXTERNAL_SUBCONTRACTOR_TRADE, and EXTERNAL_SUBCONTRACTOR_PM
    const isTrade = role?.includes('TRADE') || role === 'EXTERNAL_SUBCONTRACTOR_PM';
    const isConsultantOrOwner = ['EXTERNAL_CONSULTANT', 'EXTERNAL_OWNER'].includes(role);
    const isExternal = role?.startsWith('EXTERNAL_');

    /**
     * Refs to access methods exposed by the ImageGallery components.
     * These refs allow us to:
     * 1. Get the list of images marked for deletion when saving
     * 2. Clear the deletion markers when canceling
     */
    const beforeImagesRef = useRef();
    const afterImagesRef = useRef();

    const afterImagesSectionRef = useRef();

    // State for tracking previous attempts UI
    const [expandedAttemptId, setExpandedAttemptId] = useState(null);

    // State for tracking edit mode
    const [isEditing, setIsEditing] = useState(false);

    // State for tracking edited deficiency data
    const [editedDeficiency, setEditedDeficiency] = useState(null);

    // State to track if images are currently uploading
    const [isUploadingImages, setIsUploadingImages] = useState(false);

    // State to track if save is in progress (prevents double-click duplicates)
    const [isSaving, setIsSaving] = useState(false);

    const attemptRefs = useRef({});

    // State for snackbar notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // State for history dialog
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

    // Filter previous attempts
    const previousAttempts = React.useMemo(() => {
        if (!deficiency?.attempts || !deficiency?.activeAttemptId) return [];
        return deficiency.attempts
            .filter((a) => a.attemptId !== deficiency.activeAttemptId)
            .sort((a, b) => (b.attemptNumber || 0) - (a.attemptNumber || 0));
    }, [deficiency]);

    // State for full-screen image viewing
    const [selectedImage, setSelectedImage] = useState(null);

    // Handle smooth scrolling when expanded attempt changes
    useEffect(() => {
        if (expandedAttemptId && attemptRefs.current[expandedAttemptId]) {
            // Small timeout to ensure Collapse animation has started/DOM is ready
            setTimeout(() => {
                attemptRefs.current[expandedAttemptId]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 100);
        }
    }, [expandedAttemptId]);

    // Navigate to attempt handler
    const handleNavigateToAttempt = useCallback((attemptId) => {
        setExpandedAttemptId(attemptId);
    }, []);

    // When opened from a rejection notification, expand and scroll to the rejected attempt
    // in the revision history (the existing expandedAttemptId effect handles the scroll).
    // Keyed per target so a second rejection notification for the same deficiency re-navigates;
    // reset on close so reopening the same target navigates again.
    const rejectionNavRef = useRef(null);
    useEffect(() => {
        if (!open) {
            rejectionNavRef.current = null;
            return;
        }
        if (!scrollToRejection || !deficiency?.deficiencyId) return;

        const targetAttemptId =
            (rejectionAttemptId &&
                previousAttempts.some((a) => a.attemptId === rejectionAttemptId) &&
                rejectionAttemptId) ||
            (previousAttempts.length > 0 ? previousAttempts[0].attemptId : null);

        if (!targetAttemptId) return;

        const navKey = `${deficiency.deficiencyId}:${targetAttemptId}`;
        if (rejectionNavRef.current === navKey) return;

        rejectionNavRef.current = navKey;
        setExpandedAttemptId(targetAttemptId);
    }, [open, scrollToRejection, rejectionAttemptId, deficiency?.deficiencyId, previousAttempts]);

    /* ---------------------------------------------------------------------- */
    /*  Handle sidebar save                                                  */
    /* ---------------------------------------------------------------------- */
    const handleSidebarSave = useCallback(
        async (updatedDeficiency) => {
            try {
                const deficiencyId = updatedDeficiency.deficiencyId;

                // Get the current deficiency from state to compare changes
                const currentDeficiency = deficiencyState.deficiencies.find(
                    (d) => d.deficiencyId === deficiencyId
                );

                if (!currentDeficiency) {
                    throw new Error(`Deficiency with ID ${deficiencyId} not found`);
                }

                // Deep clone to avoid reference issues
                const deficiencyToUpdate = JSON.parse(JSON.stringify(updatedDeficiency));

                // Handle unassignment properly
                if (deficiencyToUpdate.assignedToBusinessId === '') {
                    deficiencyToUpdate.assignedToBusinessId = null;
                }

                // decide if we need to auto update status or assignedTo
                if (
                    currentDeficiency.assignedToBusinessId !==
                    deficiencyToUpdate.assignedToBusinessId
                ) {
                    // if newValue for assignedToBusinessId is empty string
                    // we just need to make sure the assignedTo Values are null
                    if (
                        !deficiencyToUpdate.assignedToBusinessId ||
                        deficiencyToUpdate.assignedToBusinessId === ''
                    ) {
                        deficiencyToUpdate.assignedToBusinessId = null;
                        deficiencyToUpdate.assignedToBusinessName = null;
                        // If we're unassigning someone and status is currently "Assigned", revert to "Accepted"
                        if (deficiencyToUpdate.status === 'Assigned') {
                            deficiencyToUpdate.status = 'Accepted';
                        }
                    } else if (deficiencyToUpdate.status) {
                        const earlyStatuses = ['Submitted', 'Accepted'];
                        if (earlyStatuses.includes(deficiencyToUpdate.status)) {
                            deficiencyToUpdate.status = 'Assigned';
                        }
                    }
                }

                // Separate the images from the main data
                const { beforeImages, afterImages, ...deficiencyData } = deficiencyToUpdate;

                // First update the main deficiency data
                const updatedData = await updateDeficiency(deficiencyId, deficiencyData);

                // Show success notification
                setSnackbar({
                    open: true,
                    message: 'Deficiency saved successfully',
                    severity: 'success',
                });

                return updatedData;
            } catch (error) {
                // Error notification handled below
                setSnackbar({
                    open: true,
                    message: `Error: ${error.message || 'Failed to save changes'}`,
                    severity: 'error',
                });
                throw error;
            }
        },
        [deficiencyState, updateDeficiency]
    );

    // Initialize or sync edited deficiency when backend data changes or drawer opens
    useEffect(() => {
        if (!deficiency || !open) return;

        setEditedDeficiency((prev) => {
            // If we're not currently editing, or we don't have local state yet, full sync
            if (!isEditing || !prev) {
                return { ...deficiency };
            }

            // If we ARE editing, we only want to sync specific fields that might change externally
            // (like images, attempts, and status) while preserving our current draft textual edits.
            return {
                ...prev,
                beforeImages: deficiency.beforeImages,
                afterImages: deficiency.afterImages,
                attempts: deficiency.attempts,
                activeAttemptId: deficiency.activeAttemptId,
                status: deficiency.status,
            };
        });
    }, [deficiency, open, isEditing]);

    // If no deficiency is selected or drawer isn't open, don't render
    if (!deficiency || !open) return null;

    // The active deficiency data (original when viewing, edited when editing)
    const activeDeficiency = isEditing ? editedDeficiency : deficiency;

    // Get status styling
    const statusChipProps = getStatusChipProps(activeDeficiency.status, role);

    // Handle entering edit mode
    const handleEditClick = () => {
        setIsEditing(true);
        setEditedDeficiency({ ...deficiency });
    };

    // Handle notification close
    const handleCloseNotification = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Handle history dialog
    const handleHistoryClick = () => {
        setHistoryDialogOpen(true);
    };

    const handleHistoryClose = () => {
        setHistoryDialogOpen(false);
    };

    // Handle direct field updates (for dynamic fields like status and assignee)
    const handleDirectFieldUpdate = async (updatedData) => {
        // If images are still uploading, show warning instead of saving
        if (isUploadingImages) {
            setSnackbar({
                open: true,
                message: 'Please wait for images to finish uploading',
                severity: 'warning',
            });
            return;
        }

        try {
            // If status is being updated, check if we should use onStatusChange
            if (updatedData.status && onStatusChange) {
                onStatusChange(deficiency.deficiencyId, updatedData.status);
                // If it's a rejection, we stop here as DeficiencyPage will handle the modal
                if (updatedData.status === 'Rejected') return;
            }

            // Create a copy of the deficiency with the updated field
            const updatedDeficiency = {
                ...deficiency,
                ...updatedData,
            };

            // Save the updated deficiency using our internal save method
            await handleSidebarSave(updatedDeficiency);

            // Update our local state to reflect the change
            setEditedDeficiency(updatedDeficiency);
        } catch (error) {
            // Error handling is done in handleSidebarSave
        }
    };

    /**
     * Handles saving all changes including image deletions.
     * The flow is:
     * 1. Check if there are any images marked for deletion
     * 2. Call the API to delete those images
     * 3. Save the rest of the deficiency changes
     */
    const handleSave = async () => {
        // Prevent double-click duplicates
        if (isSaving) return;

        // If images are still uploading, show warning instead of saving
        if (isUploadingImages) {
            setSnackbar({
                open: true,
                message: 'Please wait for images to finish uploading',
                severity: 'warning',
            });
            return;
        }

        setIsSaving(true);

        // First, process any images marked for deletion
        const beforeImagesToDelete = beforeImagesRef.current?.getImagesToDelete() || [];
        const afterImagesToDelete = afterImagesRef.current?.getImagesToDelete() || [];

        // Set loading state if we have images to delete
        if (beforeImagesToDelete.length > 0 || afterImagesToDelete.length > 0) {
            setIsUploadingImages(true);
        }

        try {
            // Delete "before" images
            for (const imageId of beforeImagesToDelete) {
                await deleteDeficiencyImage(imageId, deficiency.deficiencyId, 'Before');
            }

            // Delete "after" images
            for (const imageId of afterImagesToDelete) {
                await deleteDeficiencyImage(imageId, deficiency.deficiencyId, 'After');
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error deleting images: ${error.message || 'Unknown error'}`,
                severity: 'error',
            });
        } finally {
            setIsUploadingImages(false);
        }

        // Continue with normal save operation using our internal save method
        try {
            const deficiencyCopy = JSON.parse(JSON.stringify(editedDeficiency));
            await handleSidebarSave(deficiencyCopy);
            setIsEditing(false);
        } catch (error) {
            // Error already handled in handleSidebarSave
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handles canceling edit mode.
     * Important: This clears any images marked for deletion without calling the API.
     */
    const handleCancelEdit = () => {
        // Clear any images marked for deletion using the methods exposed by the refs
        beforeImagesRef.current?.clearDeleteMarkers();
        afterImagesRef.current?.clearDeleteMarkers();

        // Exit edit mode and reset the edited deficiency
        setIsEditing(false);
        setEditedDeficiency({ ...deficiency });
    };

    // Handle closing the drawer
    const handleClose = () => {
        setIsEditing(false);
        setEditedDeficiency(null);
        onClose();
    };

    // Handle updating different sections of the deficiency
    const handleDescriptionChange = (data) => {
        setEditedDeficiency({
            ...editedDeficiency,
            ...data,
        });
    };

    const handleBeforeImagesChange = (images) => {
        setEditedDeficiency({
            ...editedDeficiency,
            beforeImages: images,
        });
    };

    const handleAfterImagesChange = (images) => {
        setEditedDeficiency({
            ...editedDeficiency,
            afterImages: images,
        });
    };

    // Custom image upload handlers that preserve edits
    const handleBeforeImageUpload = async (beforeImages) => {
        if (!beforeImages || !deficiency.deficiencyId) return null;

        // Set uploading flag
        setIsUploadingImages(true);

        try {
            // Upload the images
            const updated = await updateDeficiencyImages(
                deficiency.deficiencyId,
                beforeImages,
                null,
                isEditing ? editedDeficiency : null
            );

            // Bug 1 Fix: Update local state immediately if in edit mode
            if (isEditing && updated) {
                setEditedDeficiency(updated);
            }

            return updated;
        } finally {
            // Clear uploading flag when done
            setIsUploadingImages(false);
        }
    };

    const handleAfterImageUpload = async (afterImages) => {
        if (!afterImages || !deficiency.deficiencyId) return null;

        // Set uploading flag
        setIsUploadingImages(true);

        try {
            // Upload the images
            const updated = await updateDeficiencyImages(
                deficiency.deficiencyId,
                null,
                afterImages,
                isEditing ? editedDeficiency : null
            );

            // Bug 1 Fix: Update local state immediately if in edit mode
            if (isEditing && updated) {
                setEditedDeficiency(updated);
            }

            return updated;
        } finally {
            // Clear uploading flag when done
            setIsUploadingImages(false);
        }
    };

    const handleDeferredCostChange = (cost) => {
        setEditedDeficiency({
            ...editedDeficiency,
            deferredCost: cost,
        });
    };

    const handleLocationChange = (data) => {
        setEditedDeficiency({
            ...editedDeficiency,
            location: data.location,
            locationImage: data.locationImage,
        });
    };

    // const handleCommentsChange = (comments) => {
    //     setEditedDeficiency({
    //         ...editedDeficiency,
    //         comments,
    //     });
    // };

    // Update the metadata handler to support direct updates
    const handleMetadataChange = (data) => {
        // If we're in edit mode, just update the local state
        if (isEditing) {
            setEditedDeficiency({
                ...editedDeficiency,
                ...data,
            });
        } else {
            // We're not in edit mode, so this is a direct update of a dynamic field
            handleDirectFieldUpdate(data);
        }
    };

    // Handle internal only toggle
    const handleInternalOnlyToggle = (event) => {
        const isInternalOnly = event.target.checked;
        setEditedDeficiency({
            ...editedDeficiency,
            internalOnly: isInternalOnly,
        });
    };

    return (
        <>
            {/* Hide the sidebar toggle button when DeficiencySidebar is open */}
            {open && (
                <GlobalStyles
                    styles={{
                        '#sidebar-mobile-toggle': {
                            display: 'none !important',
                        },
                    }}
                />
            )}

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                sx={{
                    width: {
                        xs: '100%',
                        md: 450,
                    },
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: {
                            xs: '100%',
                            md: 450,
                        },
                        boxSizing: 'border-box',
                        backgroundColor: '#F9FAFB',
                        zIndex: 1200,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {/* Header with title and close button */}
                    <Box
                        sx={{
                            p: 2.5,
                            backgroundColor: '#FFFFFF',
                            borderBottom: '1px solid #F0F0F0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                mr: 1,
                                flexShrink: 0,
                                color: '#666666',
                                padding: 0.5,
                                '& .MuiSvgIcon-root': {
                                    fontSize: '1rem',
                                },
                            }}
                            size="small"
                            title="Close"
                            aria-label="Close deficiency details"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {/* <Typography variant="h6" sx={{ fontWeight: 600, mr: 1.5 }}>
                                 Details
                            </Typography> */}
                            <Chip
                                label={statusChipProps.label}
                                sx={{
                                    ...statusChipProps.sx,
                                    height: 28,
                                    fontSize: '0.85rem',
                                    flexShrink: 0,
                                }}
                            />
                            {/* Internal Only toggle - only internal managers can toggle, and only if internally created */}
                            {isEditing &&
                            activeDeficiency.internallyCreated &&
                            isManager &&
                            !isExternal ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={activeDeficiency.internalOnly || false}
                                            onChange={handleInternalOnlyToggle}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#1652F0',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                    {
                                                        backgroundColor: '#1652F0',
                                                    },
                                            }}
                                        />
                                    }
                                    label="Internal Only"
                                    sx={{
                                        margin: 0,
                                        flexShrink: 0,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                        },
                                    }}
                                />
                            ) : (
                                /* Show chip only if internalOnly AND user is manager (external users shouldn't see internal only flag) */
                                activeDeficiency.internalOnly &&
                                isManager && (
                                    <Chip
                                        label="Internal Only"
                                        color="default"
                                        variant="outlined"
                                        size="small"
                                        sx={{ flexShrink: 0 }}
                                    />
                                )
                            )}
                            <Tooltip
                                title={activeDeficiency.title || 'Untitled Deficiency'}
                                enterDelay={200}
                                arrow
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        color: '#333333',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        ml: 0.5,
                                    }}
                                >
                                    {activeDeficiency.title || 'Untitled Deficiency'}
                                </Typography>
                            </Tooltip>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            {/* History button - only managers */}
                            {isManager && !isEditing && (
                                <IconButton
                                    onClick={handleHistoryClick}
                                    sx={{
                                        ml: 1,
                                        color: '#666666',
                                        padding: 0.5,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                    size="small"
                                    title="View History"
                                >
                                    <HistoryIcon />
                                </IconButton>
                            )}
                            {/* Edit button - only managers */}
                            {isManager && !readOnly && !isEditing && (
                                <IconButton
                                    onClick={handleEditClick}
                                    sx={{
                                        ml: 1,
                                        color: '#666666',
                                        padding: 0.5,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                    size="small"
                                    title="Edit"
                                >
                                    <EditIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                    {/* Next Action Button section - managers, trades, and consultants/owners */}
                    {/* Managers: Full actions, Trades: Limited to upload/mark complete, Consultants/Owners: Final Approval only */}
                    {!isEditing &&
                        activeDeficiency &&
                        (isManager || isTrade || isConsultantOrOwner) && (
                            <NextActionButton
                                deficiency={activeDeficiency}
                                renderSection
                                sx={{ width: '100%' }}
                            />
                        )}

                    {/* Notification Banner for this deficiency */}
                    {!isEditing && activeDeficiency && (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: '#FFFFFF',
                                borderBottom: '1px solid #F0F0F0',
                            }}
                        >
                            <NotificationBanner
                                deficiencyId={activeDeficiency.deficiencyId}
                                dueDateState={activeDeficiency.dueDateState}
                                variant="sidebar"
                                showActions={true}
                            />
                        </Box>
                    )}

                    {/* Fallback for custom header actions if provided */}
                    {!isEditing && headerActions && (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: '#FFFFFF',
                                borderBottom: '1px solid #F0F0F0',
                            }}
                        >
                            {headerActions}
                        </Box>
                    )}

                    {/* Content area (scrollable) */}
                    <Box
                        sx={{
                            p: 2.5,
                            flex: 1,
                            overflowY: 'auto',
                        }}
                    >
                        {/* Due Date - simple read-only display for trades who don't see the full StatusTracker */}
                        {isTrade && (
                            <Box
                                sx={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    border: '1px solid #F0F0F0',
                                    p: 2.5,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 2.5,
                                }}
                            >
                                <CalendarTodayIcon sx={{ fontSize: '1rem', color: '#888888' }} />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#555555' }}
                                >
                                    Due Date
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: activeDeficiency.dueDate ? '#333333' : '#AAAAAA',
                                        ml: 'auto',
                                    }}
                                >
                                    {activeDeficiency.dueDate
                                        ? formatDate(activeDeficiency.dueDate)
                                        : 'Not set'}
                                </Typography>
                            </Box>
                        )}

                        {/* Description section - title/description view only for non-managers */}
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1px solid #F0F0F0',
                                p: 2.5,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            }}
                        >
                            <DescriptionSection
                                deficiency={activeDeficiency}
                                isEditing={isEditing && isManager}
                                onChange={handleDescriptionChange}
                            />

                            {/* Deferred Cost Section - only deferred-cost-permitted roles (EXT-LAUNCH-009):
                                internal PM/admin (editable) and external sub PM (read-only).
                                Hidden for internal super, trades, consultants, and owners. */}
                            {canSeeDeferredCost(role) && (
                                <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #F0F0F0' }}>
                                    <DeferredCostSection
                                        deficiency={activeDeficiency}
                                        isEditing={isEditing}
                                        onChange={handleDeferredCostChange}
                                        readOnly={!isManager}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Before Images */}
                        {/* All roles can view, managers can upload/delete, consultants/owners can add if submitting */}
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1px solid #F0F0F0',
                                p: 2.5,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            }}
                        >
                            <ImageGallery
                                ref={beforeImagesRef}
                                imageType="Before"
                                images={activeDeficiency.beforeImages || []}
                                isEditing={isEditing}
                                onChange={handleBeforeImagesChange}
                                onUpload={handleBeforeImageUpload}
                                deficiencyId={activeDeficiency.deficiencyId}
                                onDeleteImage={deleteDeficiencyImage}
                                readOnly={!isManager}
                            />
                        </Box>

                        {/* After Images */}
                        {/* Managers and trades can upload/delete, consultants/owners view only */}
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1px solid #F0F0F0',
                                p: 2.5,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            }}
                            ref={afterImagesSectionRef}
                            data-section="after-images"
                        >
                            <ImageGallery
                                ref={afterImagesRef}
                                imageType="After"
                                images={activeDeficiency.afterImages || []}
                                isEditing={isEditing}
                                onChange={handleAfterImagesChange}
                                deficiencyId={activeDeficiency.deficiencyId}
                                onUpload={handleAfterImageUpload}
                                onDeleteImage={deleteDeficiencyImage}
                                readOnly={!(isManager || isTrade)}
                            />

                            {!isEditing && previousAttempts.length > 0 && (
                                <Box sx={{ mt: 3, borderTop: '1px solid #F5F5F5', pt: 2.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#999999',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            display: 'block',
                                            mb: 2,
                                        }}
                                    >
                                        Revision History ({previousAttempts.length})
                                    </Typography>

                                    <Stack spacing={1.5}>
                                        {previousAttempts.map((attempt) => (
                                            <Box
                                                key={attempt.attemptId}
                                                ref={(el) =>
                                                    (attemptRefs.current[attempt.attemptId] = el)
                                                }
                                                sx={{
                                                    borderRadius: '8px',
                                                    border: '1px solid #F0F0F0',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor:
                                                        expandedAttemptId === attempt.attemptId
                                                            ? '#F8F9FF'
                                                            : 'transparent',
                                                    borderColor:
                                                        expandedAttemptId === attempt.attemptId
                                                            ? '#E0E7FF'
                                                            : '#F0F0F0',
                                                }}
                                            >
                                                <Box
                                                    onClick={() =>
                                                        setExpandedAttemptId(
                                                            expandedAttemptId === attempt.attemptId
                                                                ? null
                                                                : attempt.attemptId
                                                        )
                                                    }
                                                    sx={{
                                                        p: 1.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        cursor: 'pointer',
                                                        '&:hover': { backgroundColor: '#FBFCFF' },
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1.5}
                                                        alignItems="center"
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: '6px',
                                                                backgroundColor: '#F0F0F0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 700,
                                                                color: '#666666',
                                                            }}
                                                        >
                                                            #{attempt.attemptNumber}
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#333333',
                                                                }}
                                                            >
                                                                Rejected Attempt
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: '#888888',
                                                                    display: 'block',
                                                                }}
                                                            >
                                                                By{' '}
                                                                {attempt.rejectedByName || 'Admin'}{' '}
                                                                •{' '}
                                                                {new Date(
                                                                    attempt.rejectedAt
                                                                ).toLocaleDateString()}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#BBBBBB' }}
                                                    >
                                                        {expandedAttemptId === attempt.attemptId ? (
                                                            <ExpandLessIcon fontSize="small" />
                                                        ) : (
                                                            <ExpandMoreIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Box>

                                                <Collapse
                                                    in={expandedAttemptId === attempt.attemptId}
                                                >
                                                    <Box sx={{ p: 2, pt: 0, borderTop: 'none' }}>
                                                        {attempt.rejectionReason && (
                                                            <Box
                                                                sx={{
                                                                    mb: 2,
                                                                    p: 1.5,
                                                                    borderRadius: '8px',
                                                                    backgroundColor: '#FFF5F5',
                                                                    border: '1px solid #FFEBEB',
                                                                    display: 'flex',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        color: '#FF4D4D',
                                                                        mt: 0.2,
                                                                    }}
                                                                >
                                                                    <InfoIcon fontSize="small" />
                                                                </Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: '#D32F2F',
                                                                        lineHeight: 1.5,
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    {attempt.rejectionReason}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {attempt.images &&
                                                            attempt.images.length > 0 && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color: '#888888',
                                                                            display: 'block',
                                                                            mb: 1,
                                                                        }}
                                                                    >
                                                                        SUBMITTED IMAGES (
                                                                        {attempt.images.length})
                                                                    </Typography>
                                                                    <Grid container spacing={1}>
                                                                        {attempt.images.map(
                                                                            (img) => (
                                                                                <Grid
                                                                                    key={
                                                                                        img.deficiencyImageId
                                                                                    }
                                                                                >
                                                                                    <Box
                                                                                        component="img"
                                                                                        src={getImageUrl(
                                                                                            img.imageUrl
                                                                                        )}
                                                                                        sx={{
                                                                                            width: 56,
                                                                                            height: 56,
                                                                                            borderRadius:
                                                                                                '6px',
                                                                                            objectFit:
                                                                                                'cover',
                                                                                            border: '1px solid #EEEEEE',
                                                                                            cursor: 'pointer',
                                                                                            transition:
                                                                                                'transform 0.2s',
                                                                                            '&:hover':
                                                                                                {
                                                                                                    transform:
                                                                                                        'scale(1.05)',
                                                                                                    borderColor:
                                                                                                        '#1652F0',
                                                                                                },
                                                                                        }}
                                                                                        onClick={(
                                                                                            e
                                                                                        ) => {
                                                                                            e.stopPropagation();
                                                                                            setSelectedImage(
                                                                                                getImageUrl(
                                                                                                    img.imageUrl
                                                                                                )
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                            )
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            )}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>

                        {/* Location section - visible to all roles */}
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1px solid #F0F0F0',
                                p: 2.5,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                            }}
                        >
                            <LocationSection
                                deficiency={activeDeficiency}
                                isEditing={isEditing && isManager}
                                onChange={handleLocationChange}
                            />
                        </Box>

                        {/* Status Tracker - managers and consultants/owners (read-only for non-managers) */}
                        {/* Hidden for trades */}
                        {(isManager || isConsultantOrOwner) && (
                            <Box
                                sx={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    border: '1px solid #F0F0F0',
                                    p: 2.5,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                }}
                            >
                                <StatusTracker
                                    deficiency={activeDeficiency}
                                    isEditing={isEditing}
                                    onChange={handleMetadataChange}
                                    readOnly={!isManager}
                                    role={role}
                                />
                            </Box>
                        )}

                        {/* Comments section */}
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '12px',
                                border: '1px solid #F0F0F0',
                                p: 2.5,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                mb: 2,
                            }}
                        >
                            <CommentsSection
                                deficiency={activeDeficiency}
                                onNavigateToAttempt={handleNavigateToAttempt}
                            />
                        </Box>
                    </Box>

                    {/* Footer with action buttons when in edit mode */}
                    {isEditing && (
                        <Box
                            sx={{
                                p: 2.5,
                                backgroundColor: '#FFFFFF',
                                borderTop: '1px solid #F0F0F0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{
                                    color: '#666666',
                                    borderColor: '#CCCCCC',
                                    '&:hover': {
                                        borderColor: '#999999',
                                        backgroundColor: '#F5F5F5',
                                    },
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                }}
                                disabled={isUploadingImages || isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                sx={{
                                    backgroundColor: '#1652F0',
                                    '&:hover': {
                                        backgroundColor: '#0A46E4',
                                    },
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                }}
                                disabled={isUploadingImages || isSaving}
                            >
                                {isSaving
                                    ? 'Saving...'
                                    : isUploadingImages
                                      ? 'Uploading Images...'
                                      : 'Save Changes'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Drawer>

            {/* Image Modal (Lightbox) for History and general viewing */}
            <Modal
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 300,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        maxWidth: '90%',
                        maxHeight: '90%',
                        outline: 'none',
                        p: 1,
                    }}
                >
                    <IconButton
                        onClick={() => setSelectedImage(null)}
                        sx={{
                            position: 'absolute',
                            top: -45,
                            right: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Full size"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: 4,
                                boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
                            }}
                            onError={(e) => {
                                e.target.src =
                                    'https://via.placeholder.com/800x600?text=Image+Error';
                            }}
                        />
                    )}
                </Box>
            </Modal>

            {/* Unified snackbar for all notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        boxShadow: 3,
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Deficiency History Dialog */}
            <DeficiencyHistory
                open={historyDialogOpen}
                onClose={handleHistoryClose}
                deficiency={deficiency}
            />
        </>
    );
};

export default DeficiencySidebar;
